import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator,
  Download,
  FileText,
  Eye,
  History,
  Settings,
  Loader,
  Users,
  DollarSign,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../context/AuthContext';

const PayrollReport = ({
  staff,
  payrollReports,
  loading,
  generatePayrollReport,
  fetchPayrollReports,
  setError,
  setSuccess
}) => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken');
  };

  // State management - simplified to match backend
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [customPayrollLoading, setCustomPayrollLoading] = useState(false);

  // Get current month in YYYY-MM format
  const getCurrentPeriod = () => {
    const now = new Date();
    const lagosNow = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Lagos" }));
    return `${lagosNow.getFullYear()}-${String(lagosNow.getMonth() + 1).padStart(2, '0')}`;
  };

  // Load available periods from backend
  const loadAvailablePeriods = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/payroll/available-periods`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailablePeriods(data.periods || []);
        
        // Auto-select current period if available
        const currentPeriod = getCurrentPeriod();
        if (data.periods.includes(currentPeriod)) {
          setSelectedPeriod(currentPeriod);
        } else if (data.periods.length > 0) {
          // Select most recent period available
          setSelectedPeriod(data.periods[0]);
        }
        
        console.log('Available periods loaded:', data.periods);
      }
    } catch (err) {
      console.error('Error loading available periods:', err);
      setError('Failed to load available periods');
    }
  }, [setError]);

  // Generate payroll report - bow to backend completely
  const generateCustomPayrollReport = useCallback(async () => {
    if (!selectedPeriod || customPayrollLoading) return;
    
    try {
      setCustomPayrollLoading(true);
      setError('');
      
      // SIMPLE: Just send period to backend, let it handle everything
      console.log('Generating payroll report for period:', selectedPeriod);

      const response = await generatePayrollReport({
        period: selectedPeriod
      });
      
      setCurrentReport(response);
      setSuccess('Payroll report generated successfully');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error generating payroll report:', err);
      if (err.message && err.message.includes('404')) {
        setError(`No data available for ${selectedPeriod}. Try running historical recalculation.`);
      } else {
        setError('Failed to generate payroll report');
      }
      setCurrentReport(null);
    } finally {
      setCustomPayrollLoading(false);
    }
  }, [selectedPeriod, generatePayrollReport, setError, setSuccess]);

  // Load available periods on mount
  useEffect(() => {
    loadAvailablePeriods();
  }, [loadAvailablePeriods]);

  // Auto-generate report when period changes
  useEffect(() => {
    if (selectedPeriod) {
      generateCustomPayrollReport();
    }
  }, [selectedPeriod, generateCustomPayrollReport]);

  // Format currency
  const formatCurrency = (amount) => `₦${Math.round(amount || 0).toLocaleString('en-NG')}`;

  // Format period for display
  const formatPeriodDisplay = (period) => {
    if (!period) return '';
    const [year, month] = period.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Currency formatting function for PDF exports
  const formatCurrencyForPDF = (amount) => {
    if (!amount || isNaN(amount)) return 'N0';
    return 'N' + Math.round(amount).toLocaleString('en-US');
  };

  // Calculate net pay with proper handling
  const calculateNetPay = useCallback((employee) => {
    const grossPay = (employee.baseSalary || 0) + (employee.totalAllowances || 0);
    const totalDeductions = (employee.totalDeductions || 0) + (employee.taxAmount || 0) + (employee.pensionAmount || 0);
    
    // Check if backend provided a pre-calculated net pay
    if (employee.netPay !== undefined && employee.netPay !== null) {
      return Math.max(0, employee.netPay);
    }
    
    // Fallback calculation
    if (employee.isPartialPeriod && !employee.isMonthComplete && employee.actualWorkingDays > 0) {
      const prorationFactor = employee.actualWorkingDays / (employee.standardWorkingDays || 26);
      const proRatedGross = grossPay * prorationFactor;
      return Math.max(0, proRatedGross - totalDeductions);
    }
    
    return Math.max(0, grossPay - totalDeductions);
  }, []);

  // Proration Notice Component
  const ProrationNotice = ({ report }) => {
    if (!report || (!report.isPartialPeriod && !report.payrollData?.some(p => p.isPartialPeriod))) return null;
    
    const avgWorkingDays = report.actualWorkingDays || 0;
    const standardDays = report.dynamicWorkingDays || 26;
    const completionPercentage = standardDays > 0 ? Math.round((avgWorkingDays / standardDays) * 100) : 0;
    
    return (
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-800 mb-2">
              Prorated Period - Month Not Complete
            </h4>
            <div className="space-y-2 text-sm text-yellow-700">
              <p>
                <strong>Working Days Completed:</strong> {avgWorkingDays} of {standardDays} days 
                ({completionPercentage}%)
              </p>
              <p>
                <strong>Net Pay:</strong> Prorated based on working days completed for incomplete month
              </p>
            </div>
            
            <div className="mt-3">
              <div className="flex justify-between text-xs text-yellow-600 mb-1">
                <span>Month Progress</span>
                <span>{completionPercentage}%</span>
              </div>
              <div className="w-full bg-yellow-200 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Export payroll report function
  const exportPayrollReport = useCallback((format) => {
    if (!currentReport) {
      setError('No payroll data to export');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const reportTitle = `Payroll Report - ${formatPeriodDisplay(selectedPeriod)} (${selectedPeriod})`;

    if (format === 'pdf') {
      try {
        const doc = new jsPDF();
        
        // Title and header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(reportTitle, 14, 20);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
        
        // Proration notice if applicable
        if (currentReport.isPartialPeriod) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'italic');
          doc.text(`Note: Prorated period - ${currentReport.actualWorkingDays} of ${currentReport.dynamicWorkingDays} working days`, 14, 40);
        }

        // Summary table
        const summaryData = [
          ['Metric', 'Value'],
          ['Total Staff', currentReport.totalStaff.toString()],
          ['Total Base Salary', formatCurrencyForPDF(currentReport.totalBaseSalary)],
          ['Total Allowances', formatCurrencyForPDF(currentReport.totalAllowances)],
          ['Total Gross Pay', formatCurrencyForPDF(currentReport.totalGrossPay)],
          ['Total Deductions', formatCurrencyForPDF(currentReport.totalDeductions)],
          ['Total Tax', formatCurrencyForPDF(currentReport.totalTax)],
          ['Total Pension', formatCurrencyForPDF(currentReport.totalPension || 0)],
          ['Total Net Pay', formatCurrencyForPDF(currentReport.totalNetPay)]
        ];

        autoTable(doc, {
          startY: currentReport.isPartialPeriod ? 50 : 40,
          head: [summaryData[0]],
          body: summaryData.slice(1),
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 10, cellPadding: 4 }
        });

        // Employee details table
        const employeeTableData = currentReport.payrollData.map(employee => [
          `${employee.employeeName}\n(${employee.employeeId})\n${employee.position}`,
          formatCurrencyForPDF(employee.baseSalary),
          formatCurrencyForPDF(employee.totalAllowances),
          formatCurrencyForPDF(employee.grossPay),
          formatCurrencyForPDF(employee.totalDeductions),
          formatCurrencyForPDF(employee.taxAmount),
          formatCurrencyForPDF(employee.pensionAmount || 0),
          formatCurrencyForPDF(calculateNetPay(employee))
        ]);

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 15,
          head: [['Employee', 'Base Salary', 'Allowances', 'Gross', 'Deductions', 'Tax', 'Pension', 'Net Pay']],
          body: employeeTableData,
          theme: 'striped',
          headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', fontSize: 7 },
          styles: { fontSize: 6, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 35, fontStyle: 'bold' },
            1: { halign: 'right', cellWidth: 22 },
            2: { halign: 'right', cellWidth: 18 },
            3: { halign: 'right', cellWidth: 22 },
            4: { halign: 'right', cellWidth: 18 },
            5: { halign: 'right', cellWidth: 15 },
            6: { halign: 'right', cellWidth: 15 },
            7: { halign: 'right', cellWidth: 25, fillColor: [220, 252, 231], fontStyle: 'bold' }
          }
        });

        doc.save(`${reportTitle}.pdf`);
        setSuccess('PDF exported successfully');
      } catch (error) {
        console.error('PDF generation error:', error);
        setError('Failed to generate PDF. Please try again.');
      }
    } else if (format === 'excel') {
      try {
        const workbook = XLSX.utils.book_new();
        
        // Summary Sheet
        const summaryData = [
          [reportTitle],
          [`Generated: ${new Date().toLocaleDateString()}`],
          [],
          ['PAYROLL SUMMARY'],
          ['Metric', 'Value'],
          ['Total Staff', currentReport.totalStaff],
          ['Total Base Salary', currentReport.totalBaseSalary],
          ['Total Allowances', currentReport.totalAllowances],
          ['Total Gross Pay', currentReport.totalGrossPay],
          ['Total Deductions', currentReport.totalDeductions],
          ['Total Tax', currentReport.totalTax],
          ['Total Pension', currentReport.totalPension || 0],
          ['Total Net Pay', currentReport.totalNetPay]
        ];

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        // Employee Details Sheet
        const employeeHeaders = [
          'Employee Name', 'Employee ID', 'Position', 'Department',
          'Base Salary', 'Housing Allowance', 'Transport Allowance', 
          'Meal Allowance', 'Other Allowances', 'Total Allowances',
          'Gross Pay', 'Total Deductions', 'Tax Amount', 'Pension Amount',
          'Net Pay', 'Present Days', 'Absent Days'
        ];

        const employeeData = [
          employeeHeaders,
          ...currentReport.payrollData.map(employee => [
            employee.employeeName,
            employee.employeeId,
            employee.position,
            employee.department,
            employee.baseSalary,
            employee.housingAllowance,
            employee.transportAllowance,
            employee.mealAllowance,
            employee.otherAllowances,
            employee.totalAllowances,
            employee.grossPay,
            employee.totalDeductions,
            employee.taxAmount,
            employee.pensionAmount || 0,
            calculateNetPay(employee),
            employee.presentDays || 0,
            employee.absentDays || 0
          ])
        ];

        const employeeSheet = XLSX.utils.aoa_to_sheet(employeeData);
        XLSX.utils.book_append_sheet(workbook, employeeSheet, 'Employee Details');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, `${reportTitle}.xlsx`);
        setSuccess('Excel exported successfully');
      } catch (error) {
        console.error('Excel generation error:', error);
        setError('Failed to generate Excel file. Please try again.');
      }
    }

    setTimeout(() => setSuccess(''), 3000);
  }, [currentReport, selectedPeriod, formatPeriodDisplay, calculateNetPay, setError, setSuccess]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 flex items-center gap-2">
          <Calculator className="text-blue-600" size={24} />
          Monthly Payroll Report
        </h2>

        {/* Simplified Period Selection - Bow to Backend */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Settings className="text-green-600" size={20} />
            Generate Payroll Report
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Period Selection - Backend Dictates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <History className="inline mr-1" size={16} />
                Available Periods
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm"
                disabled={availablePeriods.length === 0}
              >
                <option value="">Select period...</option>
                {availablePeriods.map(period => (
                  <option key={period} value={period}>
                    {formatPeriodDisplay(period)} ({period})
                  </option>
                ))}
              </select>
              {availablePeriods.length === 0 && (
                <p className="text-xs text-red-500 mt-1">No periods available. Run historical recalculation.</p>
              )}
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  loadAvailablePeriods();
                  if (selectedPeriod) generateCustomPayrollReport();
                }}
                disabled={customPayrollLoading}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {customPayrollLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Refresh Report
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Period Info */}
          {selectedPeriod && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Selected Period:</span>
                <span className="font-semibold text-green-700">
                  {formatPeriodDisplay(selectedPeriod)} ({selectedPeriod})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {customPayrollLoading && (
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <div className="flex items-center justify-center gap-3 p-4 py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              <span className="text-gray-600">Generating comprehensive payroll report for {selectedPeriod}...</span>
            </div>
          </div>
        )}

        {/* No Period Selected */}
        {!selectedPeriod && !customPayrollLoading && (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <Calculator className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">Select Period for Report</h3>
            <p className="text-gray-500 mb-4">Choose from available periods to generate payroll report</p>
          </div>
        )}

        {/* Report Display */}
        {currentReport && !customPayrollLoading && (
          <>
            <ProrationNotice report={currentReport} />

            {/* Export Section */}
            <div className="flex justify-end gap-3 mb-8">
              <button
                onClick={() => exportPayrollReport('pdf')}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <FileText size={18} />
                Export PDF
              </button>
              <button
                onClick={() => exportPayrollReport('excel')}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download size={18} />
                Export Excel
              </button>
              <button 
                onClick={() => window.print()}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Eye size={18} />
                Print Preview
              </button>
            </div>

            {/* Payroll Summary */}
            <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-green-50 rounded-xl p-8 mb-8 border-2 border-blue-200">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2">
                <Calculator className="text-blue-600" size={28} />
                Payroll Summary: {formatPeriodDisplay(selectedPeriod)}
                {currentReport.isPartialPeriod && (
                  <span className="text-sm font-normal bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full ml-3">
                    Pro-rated: {currentReport.actualWorkingDays}/{currentReport.dynamicWorkingDays} days
                  </span>
                )}
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <Users className="mx-auto mb-2 text-gray-700" size={24} />
                  <p className="text-sm text-gray-600 mb-1">Staff</p>
                  <p className="text-lg font-semibold text-gray-900">{currentReport.totalStaff}</p>
                </div>
                <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-green-100">
                  <DollarSign className="mx-auto mb-2 text-green-600" size={24} />
                  <p className="text-sm text-gray-600 mb-1">Gross Pay</p>
                  <p className="text-lg font-semibold text-green-600">{formatCurrency(currentReport.totalGrossPay)}</p>
                </div>
                <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-red-100">
                  <TrendingDown className="mx-auto mb-2 text-red-600" size={24} />
                  <p className="text-sm text-gray-600 mb-1">Deductions</p>
                  <p className="text-lg font-semibold text-red-600">{formatCurrency(currentReport.totalDeductions)}</p>
                </div>
                <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-orange-100">
                  <AlertCircle className="mx-auto mb-2 text-orange-600" size={24} />
                  <p className="text-sm text-gray-600 mb-1">Tax</p>
                  <p className="text-lg font-semibold text-orange-600">{formatCurrency(currentReport.totalTax)}</p>
                </div>
                <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-blue-100">
                  <CheckCircle className="mx-auto mb-2 text-blue-600" size={24} />
                  <p className="text-sm text-gray-600 mb-1">Net Pay</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(currentReport.totalNetPay)}</p>
                </div>
              </div>
            </div>

            {/* Payroll Table */}
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                      <th className="p-3 text-left font-bold text-sm lg:text-base min-w-[200px]">Employee</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[100px]">Base</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[100px]">Allow.</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[100px]">Gross</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[100px]">Deduct.</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[80px]">Tax</th>
                      <th className="p-3 text-right font-bold text-sm lg:text-base min-w-[120px]">Net Pay</th>
                    </tr>
                  </thead>
                  
                  <tbody>
                    {currentReport.payrollData.map((employee, index) => (
                      <tr key={index} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 border-b border-gray-100">
                        {/* Employee Column */}
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm">
                              {employee.employeeName.charAt(0)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-gray-900 text-sm lg:text-base truncate">{employee.employeeName}</p>
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="bg-blue-100 px-2 py-0.5 rounded-full text-blue-700 text-xs font-medium">
                                  {employee.employeeId}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 truncate">{employee.position}</p>
                            </div>
                          </div>
                        </td>
                        
                        {/* Base Salary Column */}
                        <td className="p-3 text-right">
                          <div className="bg-gray-50 px-2 py-1 rounded border border-gray-200 text-xs lg:text-sm">
                            <span className="font-bold text-gray-700">{formatCurrency(employee.baseSalary)}</span>
                          </div>
                        </td>
                        
                        {/* Allowances Column */}
                        <td className="p-3 text-right">
                          <div className="bg-green-50 px-2 py-1 rounded border border-green-200 text-xs lg:text-sm">
                            <span className="font-semibold text-green-600">{formatCurrency(employee.totalAllowances)}</span>
                          </div>
                        </td>
                        
                        {/* Gross Pay Column */}
                        <td className="p-3 text-right">
                          <div className="bg-blue-50 px-2 py-1 rounded border border-blue-200 text-xs lg:text-sm">
                            <span className="font-bold text-blue-700">{formatCurrency(employee.grossPay)}</span>
                          </div>
                        </td>
                        
                        {/* Deductions Column */}
                        <td className="p-3 text-right">
                          <div className="bg-red-50 px-2 py-1 rounded text-xs lg:text-sm">
                            <span className="font-semibold text-red-600">{formatCurrency(employee.totalDeductions)}</span>
                          </div>
                        </td>
                        
                        {/* Tax Column */}
                        <td className="p-3 text-right">
                          <div className="bg-orange-50 px-2 py-1 rounded text-xs lg:text-sm">
                            <span className="font-semibold text-orange-600">{formatCurrency(employee.taxAmount)}</span>
                          </div>
                        </td>
                        
                        {/* Net Pay Column */}
                        <td className="p-3 text-right">
                          <div className="bg-gradient-to-r from-blue-100 to-purple-100 px-2 py-1 rounded border border-blue-200 text-xs lg:text-sm">
                            <span className="font-bold text-blue-800">{formatCurrency(calculateNetPay(employee))}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            </div>
          </>
        )}

        {/* No Data Found */}
        {!customPayrollLoading && selectedPeriod && !currentReport && (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <Calculator className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No Data Available</h3>
            <p className="text-gray-500 mb-4">No payroll data found for {formatPeriodDisplay(selectedPeriod)}</p>
            <p className="text-gray-400">Try running historical recalculation</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollReport;